import React from "react";
import { Input } from "../../../../shared/ui/controls/Input/Input";
import { FieldWrapper } from "../../../../shared/ui";
import { FilterBar } from "../../../../shared/ui/layouts/FilterBar";
import {
  toWorkOrderFiltersForm,
  useWorkOrderFiltersParams,
} from "../../../../features/work-order";
import type { WorkOrderFiltersForm } from "../../../../features/work-order";
import { workOrderColumns } from "../model/constants";
import { WorkOrderStatusSelect } from "src/entities/work-order";
import { GewerkSearchSelect } from "src/entities/gewerk";

type StringFilterKey = {
  [K in keyof WorkOrderFiltersForm]: WorkOrderFiltersForm[K] extends string ? K : never;
}[keyof WorkOrderFiltersForm];

const FIELD_LABELS_BEFORE_GEWERK: Array<{
  key?: StringFilterKey;
  label: string;
  sortKey?: WorkOrderFiltersForm["sortField"];
  type?: string
}> = [
    { key: "workOrderId", label: "Auftrag-ID", sortKey: "workOrderId" },
    { key: "mieter", label: "Mieter", sortKey: "mieter" },
    { key: "handwerker", label: "Handwerker", sortKey: "handwerker" },
  ];

const FIELD_LABELS_AFTER_GEWERK: Array<{
  key?: StringFilterKey;
  label: string;
  sortKey?: WorkOrderFiltersForm["sortField"];
  type?: string
}> = [
    { key: "datum", label: "Datum", sortKey: "datum", type: 'date' },
    { key: "ve", label: "VE", sortKey: "ve" },
    { key: "hauseingang", label: "Gebäude", sortKey: "hauseingang" },
    { key: "wohnung", label: "Wohnung", sortKey: "wohnung" },
  ];

export const WorkOrderFilterBar: React.FC = () => {
  const [params, setParams] = useWorkOrderFiltersParams();
  const filters = toWorkOrderFiltersForm(params);

  const updateField = (field: keyof WorkOrderFiltersForm, value: string) => {
    void setParams({
      ...filters,
      [field]: value,
    });
  };

  const handleSortChange = (
    field: WorkOrderFiltersForm["sortField"],
    direction: WorkOrderFiltersForm["sortDirection"],
  ) => {
    void setParams({
      ...filters,
      sortField: field,
      sortDirection: direction,
    });
  };

  const renderField = (
    field: { key?: StringFilterKey; label: string; sortKey?: WorkOrderFiltersForm["sortField"]; type?: string },
    index: number,
    firstIndex: boolean,
  ) => {
    const key = field.key;
    return (
      <FieldWrapper
        label={field.label}
        key={key}
        sortKey={field.sortKey}
        activeSortField={filters.sortField}
        sortOrder={filters.sortDirection}
        onSortChange={(sortKey, direction) =>
          handleSortChange(
            sortKey as WorkOrderFiltersForm["sortField"],
            direction as WorkOrderFiltersForm["sortDirection"],
          )
        }
        className={firstIndex && index === 0 ? "pl-[15px]" : undefined}
      >
        {key &&
          <Input
            as="input"
            value={filters[key]}
            onChange={(value) => updateField(key, value)}
            placeholder=""
            type={field.type ?? 'text'}
          />
        }
      </FieldWrapper>
    );
  };

  return (
    <FilterBar columns={workOrderColumns} gap={12} rowGap={8}>
      {FIELD_LABELS_BEFORE_GEWERK.map((field, index) => renderField(field, index, true))}

      <FieldWrapper label="Gewerk">
        <GewerkSearchSelect
          value={filters.gewerkId}
          onChange={(id) => void setParams({ ...filters, gewerkId: id })}
        />
      </FieldWrapper>

      {FIELD_LABELS_AFTER_GEWERK.map((field, index) => renderField(field, index, false))}

      <FieldWrapper label="Status" className="pr-[15px]">
        <WorkOrderStatusSelect
          value={filters.status}
          onChange={(value) => void setParams({ ...filters, status: value })}
          withAllOption
        />
      </FieldWrapper>
    </FilterBar>
  );
};
